import { AccessRightTabs, ObjectTypes } from '@fi-shared/types';
import { ILightWeightObject } from '@fi-shared/models/index';
import { ModuleAccessLevel } from '@fi-shared/access-right-filters/module-access-level';
import { IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { AvailableDateTimeLocale } from '@fi-shared/utils';

export interface ISelectionAccessRightFilterCollection {
    AccessLevel: number;
    IsExtendedAccessSession: boolean;
    //IsValidationRequired: boolean;
    AccessRightCoreFilter: IAccessRightCoreFilter;
    AccessRightUserFilter: IAccessRightUserFilter;
}

export interface IAccessRightSelectionFiltersCollection {
    SelectionFilterSettingCollection: AccessRightFilterCollectionModel;
    Name: string;
    IsDefault: boolean;
    Id: number;
    AccessRightFilterCollection: ISelectionAccessRightFilterCollection;
    UserId: number;
}

interface IAccessRightCreatedByItem {
    ObjectId: number;
    ObjectType: ObjectTypes;
    CreatedByUserId: number;
}

export interface IAccessRightFilterCollection {
    CreatedByData: IAccessRightCreatedByItem[];
    AccessRightCoreFilter: IAccessRightCoreFilter;
    AccessRightUserFilter: IAccessRightUserFilter;
    AccessLevel?: ModuleAccessLevel;
    SelectionMode?: AccessRightTabs;
}

export interface IAccessRightCoreFilter {
    RoleIds: number[];
    BusinessAreaIds: number[];
    BusinessUnitIds: number[];
    ClusterIds: number[];
    AreaIds: number[];

    Roles?: ILightWeightObject[];
    BusinessAreas?: ILightWeightObject[];
    Areas?: ILightWeightArea[];
    Clusters?: ILightWeightObject[];
    BusinessUnits?: ILightWeightBusinessUnit[];
}

export interface IAccessRightUserFilter {
    UserIds: number[];
    Users?: ILightWeightUser[];
}

export class AccessRightFilterCollectionModel implements IAccessRightFilterCollection {
    AccessRightCoreFilter = new AccessRightCoreFilterModel();
    AccessRightUserFilter = new AccessRightUserFilterModel();
    HasEditAccess = false;
    SelectionMode = AccessRightTabs.QuickChoice;
    AccessLevel: ModuleAccessLevel;
    CreatedByData: IAccessRightCreatedByItem[] = [];

    InitializeAccessRightFilterCollection(accessRightFilterCollection) {
        AccessRightCoreFilterModel.prototype.Initialize.call(
            this.AccessRightCoreFilter,
            accessRightFilterCollection.AccessRightCoreFilter,
        );
        AccessRightUserFilterModel.prototype.Initialize.call(
            this.AccessRightUserFilter,
            accessRightFilterCollection.AccessRightUserFilter,
        );
        this.HasEditAccess = accessRightFilterCollection.HasEditAccess;
        //this.SelectionMode = accessRightFilterCollection.SelectionMode || AccessRightTabs.QuickChoice;
    }

    IsAnyRightsSelected() {
        return this.AccessRightCoreFilter.IsAnyRightsSelected() || this.AccessRightUserFilter.IsAnyRightsSelected();
    }
    static getNewInstance(): IAccessRightFilterCollection {
        return {
            AccessRightCoreFilter: {
                RoleIds: [],
                BusinessAreaIds: [],
                BusinessUnitIds: [],
                AreaIds: [],
                ClusterIds: [],
            },
            AccessRightUserFilter: {
                UserIds: [],
            },
            CreatedByData: [],
        };
    }
    static getNewInstanceWithItems(): IAccessRightFilterCollection {
        const instance = this.getNewInstance();
        const coreFilter = instance.AccessRightCoreFilter;
        const userFilter = instance.AccessRightUserFilter;
        coreFilter.Roles =
            coreFilter.BusinessAreas =
            coreFilter.Areas =
            coreFilter.Clusters =
            coreFilter.BusinessUnits =
            userFilter.Users =
                [];
        return instance;
    }
}

export interface ILightWeightArea {
    Id: number;
    Name: string;
    ParentId: number;
    IsSystemObject: boolean;
    ChildItems: ILightWeightArea[];
    PreferredDateFormat: AvailableDateTimeLocale;
    Level: number;
}

export interface ILightWeightBusinessUnit extends ILightWeightObject {
    MediaBankPhysicalFileName: string;
    MediaBankExtension: string;
}

export interface ILightWeightUser extends IUserProfile {
    AreaId?: number;
    IsActivated?: boolean;
    ChangedOn?: string;
    Id: number;
    FullName: string;
    IsSystemObject?: boolean;
    MediaBankPhysicalFileName: string;
    MediaBankExtension: string;
    PreferredBusinessUnitName: string;
    PreferredBusinessId?: number;
}

export interface ILightWeightUserWithUnits extends ILightWeightUser {
    BusinessUnits: ILightWeightObject[];
}

export class AccessRightCoreFilterModel {
    RoleIds = [];
    BusinessAreaIds = [];
    AreaIds: number[] = [];
    ClusterIds = [];
    BusinessUnitIds = [];

    Roles: ILightWeightObject[];
    BusinessAreas: ILightWeightObject[];
    Areas: ILightWeightArea[];
    Clusters: ILightWeightObject[];
    BusinessUnits: ILightWeightBusinessUnit[];
    HasExtendedRoleAccessRights;
    Initialize(accessRightCoreFilter) {
        this.RoleIds = accessRightCoreFilter.RoleIds;
        this.Roles = accessRightCoreFilter.Roles;

        this.BusinessAreaIds = accessRightCoreFilter.BusinessAreaIds;
        this.BusinessAreas = accessRightCoreFilter.BusinessAreas;

        this.AreaIds = accessRightCoreFilter.AreaIds;
        this.Areas = accessRightCoreFilter.Areas;

        this.ClusterIds = accessRightCoreFilter.ClusterIds;
        this.Clusters = accessRightCoreFilter.Clusters;

        this.BusinessUnitIds = accessRightCoreFilter.BusinessUnitIds;
        this.BusinessUnits = accessRightCoreFilter.BusinessUnits;
    }

    IsAnyRightsSelected() {
        return (
            (this.RoleIds && this.RoleIds.length > 0) ||
            (this.BusinessAreaIds && this.BusinessAreaIds.length > 0) ||
            (this.AreaIds && this.AreaIds.length > 0) ||
            (this.ClusterIds && this.ClusterIds.length > 0) ||
            (this.BusinessUnitIds && this.BusinessUnitIds.length > 0)
        );
    }
}

export class AccessRightUserFilterModel {
    UserIds = [];
    Users: ILightWeightUser[];
    Initialize(accessRightUserFilter) {
        this.UserIds = accessRightUserFilter.UserIds;
        this.Users = accessRightUserFilter.Users;
    }

    IsAnyRightsSelected() {
        return this.UserIds && this.UserIds.length > 0;
    }
}

export class SelectionFilterModel {
    Id: number = 0;
    Name: string = '';
    IsActivated: boolean = true;
    IsDefault: boolean = false;
    IsSystem: boolean = false;
    SelectionFilterSettingCollection: AccessRightFilterCollectionModel = new AccessRightFilterCollectionModel();

    Initialize(selectionFilter) {
        this.Id = selectionFilter.Id;
        this.Name = selectionFilter.Name;
        this.IsActivated = selectionFilter.IsActivated;
        this.InitializeSelectionFilterSettingCollection(selectionFilter.SelectionFilterSettingCollection);
    }

    InitializeSelectionFilterSettingCollection(selectionFilterSettingCollection) {
        return AccessRightFilterCollectionModel.prototype.InitializeAccessRightFilterCollection.call(
            this.SelectionFilterSettingCollection,
            selectionFilterSettingCollection,
        );
    }
}
